import type { FC } from 'react';
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@packages/shared/src/ui/Card/Card';
import { Controller, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useFormatPrice } from '@packages/shared/src/hooks/useFormatPrice/useFormatPrice';
import { Checkbox } from '@packages/shared/src/ui/Checkbox/Checkbox';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogHeader } from '@packages/shared/src/ui/Dialog/DialogHeader/DialogHeader';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { ServiceLayer } from '@packages/cms-components/src/modules/ServiceLayer/ServiceLayer';
import { ControlledRadioGroupWithLabel } from '@packages/modules/src/ControlledRadioGroup/ControlledRadioGroupWithLabel';
import { RadioGroupItem } from '@packages/shared/src/ui/RadioGroup/RadioGroup';
import { Label } from '@packages/shared/src/ui/Label/Label';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { creditFormMessages as messages, generalFormMessages } from './messages';
import type { CreditCheckFormValues } from './schema';

export const CreditContractSection: FC<{ rate: string }> = ({ rate }) => {
  const {
    resetField,
    formState: { errors },
    watch,
    control,
  } = useFormContext<CreditCheckFormValues<'long'>>();
  const { formatMessage } = useIntl();
  const { formatPrice } = useFormatPrice();
  const [dialogOpen, setDialogOpen] = useState(false);

  const canPay = watch('thirdPartForm.creditContractQuestion') === 'yes';
  React.useEffect(() => {
    if (!canPay) {
      resetField('thirdPartForm.creditInformationChecked');
    }
  }, [canPay, resetField]);

  return (
    <div data-testid="credit-contract-section">
      <Card>
        <CardHeader hasDivider className="py-1">
          <CardTitle>{formatMessage(messages.creditContractHeadline)}</CardTitle>
        </CardHeader>
        <CardContent className="py-1.5">
          <ControlledRadioGroupWithLabel
            labelText={formatMessage(messages.creditContractText, {
              amount: formatPrice(parseInt(rate, 10), 'EUR'),
            })}
            control={control}
            controllerName="thirdPartForm.creditContractQuestion"
            error={!!errors.thirdPartForm?.creditContractQuestion?.message}
            helperText={errors.thirdPartForm?.creditContractQuestion?.message}
            id="creditContractQuestion"
          >
            <RadioGroupItem value="yes" label={formatMessage(generalFormMessages.yesResponse)} />
            <RadioGroupItem value="no" label={formatMessage(generalFormMessages.noResponse)} />
          </ControlledRadioGroupWithLabel>
        </CardContent>
      </Card>
      {canPay && (
        <>
          <Controller
            name="thirdPartForm.creditInformationChecked"
            control={control}
            render={({ field }) => (
              <div className="flex items-center p-1.5">
                <Checkbox
                  onCheckedChange={field.onChange}
                  checked={field.value}
                  {...field}
                  className="mr-2"
                  value="creditInformationChecked"
                  id="creditInformationChecked"
                />
                <Label className="leading-none" htmlFor="creditInformationChecked">
                  {formatMessage(messages.creditInformationLabel)}
                </Label>
              </div>
            )}
          />
          <Button
            onClick={(e) => {
              e.preventDefault();
              setDialogOpen(true);
            }}
            variant="link"
            size="base"
            className="text-size-body2 items-center justify-start px-0 py-0"
          >
            {formatMessage(messages.creditInformationDialogTrigger)}
          </Button>
          <Dialog onClose={() => setDialogOpen(false)} isOpen={dialogOpen}>
            <DialogHeader onClose={() => setDialogOpen(false)} data-testid="credit-dialog-header">
              {formatMessage(messages.creditInformationDialogHeadline)}
            </DialogHeader>
            <DialogContent>
              <div className="p-2">
                <ServiceLayer serviceLayerId="creditinformation_io" />
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
};
